/**
 * useMicRecorder — MediaRecorder state machine cho push-to-talk STT (P3-S1 Doc 43).
 *
 * State machine:
 *   idle → requesting → recording → uploading → done | error
 *                          ↓
 *                       (cancel) → idle
 *
 * v1 dùng MediaRecorder Web API (browser native, no audio lib dep).
 * Output: webm/opus blob → upload → Whisper text.
 */
import * as React from "react";

import { ApiError } from "@/api/client";
import { voiceApi, type TranscribeResult } from "@/api/voice";

export type MicState =
  | "idle"
  | "requesting"
  | "recording"
  | "uploading"
  | "error";

export interface UseMicRecorderOptions {
  /** ISO 639-1 lang code, default "vi" */
  language?: string;
  /** Max duration giây, auto-stop khi đạt. Default 60s */
  maxDurationSec?: number;
  /** Callback khi STT thành công, nhận text đã transcribe */
  onTranscribed?: (result: TranscribeResult) => void;
  /** Callback khi error */
  onError?: (message: string) => void;
}

export interface UseMicRecorderReturn {
  state: MicState;
  /** Giây đã record (chỉ valid khi state === "recording") */
  elapsedSec: number;
  /** Error message khi state === "error" */
  errorMessage: string | null;
  /** Bắt đầu record (request permission + start MediaRecorder) */
  start: () => Promise<void>;
  /** Dừng record + upload + transcribe */
  stop: () => void;
  /** Hủy bỏ record (discard, không upload) */
  cancel: () => void;
  /** Reset về idle (dùng sau khi error) */
  reset: () => void;
}

const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
];

function pickMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "audio/webm";
  for (const mt of PREFERRED_MIME_TYPES) {
    try {
      if (MediaRecorder.isTypeSupported(mt)) return mt;
    } catch {
      // ignore
    }
  }
  return "audio/webm";
}

export function useMicRecorder(
  options: UseMicRecorderOptions = {},
): UseMicRecorderReturn {
  const { language = "vi", maxDurationSec = 60, onTranscribed, onError } = options;

  const [state, setState] = React.useState<MicState>("idle");
  const [elapsedSec, setElapsedSec] = React.useState(0);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const recorderRef = React.useRef<MediaRecorder | null>(null);
  const chunksRef = React.useRef<Blob[]>([]);
  const streamRef = React.useRef<MediaStream | null>(null);
  const tickerRef = React.useRef<number | null>(null);
  const startedAtRef = React.useRef<number>(0);
  const cancelledRef = React.useRef<boolean>(false);

  const cleanupStream = React.useCallback((): void => {
    if (tickerRef.current !== null) {
      window.clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const setError = React.useCallback(
    (msg: string): void => {
      setState("error");
      setErrorMessage(msg);
      cleanupStream();
      onError?.(msg);
    },
    [cleanupStream, onError],
  );

  const reset = React.useCallback((): void => {
    cleanupStream();
    setState("idle");
    setElapsedSec(0);
    setErrorMessage(null);
    cancelledRef.current = false;
  }, [cleanupStream]);

  const start = React.useCallback(async (): Promise<void> => {
    if (state !== "idle" && state !== "error") return;
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setError("Trình duyệt không hỗ trợ ghi âm");
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      setError("MediaRecorder không khả dụng");
      return;
    }

    setState("requesting");
    setErrorMessage(null);
    cancelledRef.current = false;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      const err = e as Error;
      const msg =
        err.name === "NotAllowedError" || err.name === "PermissionDeniedError"
          ? "Cần cấp quyền microphone"
          : `Không thể truy cập mic: ${err.message || err.name}`;
      setError(msg);
      return;
    }

    streamRef.current = stream;
    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType });
    } catch {
      // Fallback no mimeType
      try {
        recorder = new MediaRecorder(stream);
      } catch (e) {
        setError(`MediaRecorder init failed: ${(e as Error).message}`);
        return;
      }
    }
    recorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = (ev: BlobEvent) => {
      if (ev.data && ev.data.size > 0) {
        chunksRef.current.push(ev.data);
      }
    };

    recorder.onerror = (ev: Event) => {
      const err = (ev as unknown as { error?: Error }).error;
      setError(`Recorder error: ${err?.message || "unknown"}`);
    };

    recorder.onstop = async () => {
      // User cancelled — discard
      if (cancelledRef.current) {
        cleanupStream();
        setState("idle");
        setElapsedSec(0);
        return;
      }

      const chunks = chunksRef.current;
      cleanupStream();

      if (chunks.length === 0) {
        setError("Không có dữ liệu âm thanh");
        return;
      }

      const blob = new Blob(chunks, { type: mimeType });
      if (blob.size < 1024) {
        setError("Bản ghi quá ngắn — thử lại");
        return;
      }

      setState("uploading");
      try {
        const result = await voiceApi.transcribe(blob, language);
        if (!result.text || result.text.trim().length === 0) {
          setError("Không nhận diện được giọng nói — thử lại to + rõ hơn");
          return;
        }
        setState("idle");
        setElapsedSec(0);
        onTranscribed?.(result);
      } catch (e) {
        const msg =
          e instanceof ApiError
            ? `STT lỗi (${e.status}): ${e.message}`
            : `STT lỗi: ${(e as Error).message}`;
        setError(msg);
      }
    };

    startedAtRef.current = Date.now();
    setElapsedSec(0);
    setState("recording");
    recorder.start();

    // Ticker — update elapsed + auto-stop khi đạt max
    tickerRef.current = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setElapsedSec(elapsed);
      if (elapsed >= maxDurationSec) {
        // Auto stop
        if (recorderRef.current && recorderRef.current.state === "recording") {
          recorderRef.current.stop();
        }
      }
    }, 250);
  }, [cleanupStream, language, maxDurationSec, onTranscribed, setError, state]);

  const stop = React.useCallback((): void => {
    if (state !== "recording") return;
    const rec = recorderRef.current;
    if (rec && rec.state === "recording") {
      cancelledRef.current = false;
      rec.stop();
    }
  }, [state]);

  const cancel = React.useCallback((): void => {
    if (state !== "recording" && state !== "requesting") return;
    cancelledRef.current = true;
    const rec = recorderRef.current;
    if (rec && rec.state === "recording") {
      rec.stop();
    } else {
      // requesting state — just cleanup
      cleanupStream();
      setState("idle");
      setElapsedSec(0);
    }
  }, [cleanupStream, state]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      cleanupStream();
    };
  }, [cleanupStream]);

  return {
    state,
    elapsedSec,
    errorMessage,
    start,
    stop,
    cancel,
    reset,
  };
}
